import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { WorkoutLength } from '../types/database.tables'

export interface UseWorkoutLengthsReturn {
  lengths: WorkoutLength[]
  isLoading: boolean
  error: string | null
  loadWorkoutLengths: (workoutId: string) => Promise<WorkoutLength[]>
  avgStrokesPerLength: number | null
}

/**
 * Hook to load workout lengths for swimming workouts
 * Fetches length-by-length data including strokes, cadence, and heart rate
 */
export const useWorkoutLengths = (): UseWorkoutLengthsReturn => {
  const [lengths, setLengths] = useState<WorkoutLength[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [avgStrokesPerLength, setAvgStrokesPerLength] = useState<number | null>(null)

  const loadWorkoutLengths = useCallback(async (workoutSessionId: string): Promise<WorkoutLength[]> => {
    if (!workoutSessionId) {
      setLengths([])
      setAvgStrokesPerLength(null)
      return []
    }

    setIsLoading(true)
    setError(null)

    try {
      // Fetch workout lengths
      const { data, error: dbError } = await supabase
        .from('workout_lengths')
        .select('*')
        .eq('workout_session_id', workoutSessionId)
        .order('length_index', { ascending: true })
      if (dbError) {
        throw new Error(dbError.message)
      }

      const lengthsData = data || []
      setLengths(lengthsData)

      // Calculate average strokes per length
      // Only include active lengths (those with strokes)
      const activeLengths = lengthsData.filter((length) => {
        // Must have strokes
        return !(!length.total_strokes || length.total_strokes === 0)
      })

      if (activeLengths.length > 0) {
        const totalStrokes = activeLengths.reduce((sum, length) => sum + (length.total_strokes || 0), 0)
        const avgStrokes = Math.round(totalStrokes / activeLengths.length)
        setAvgStrokesPerLength(avgStrokes)
      } else {
        setAvgStrokesPerLength(null)
      }

      return lengthsData
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load workout lengths'
      setError(errorMessage)
      setLengths([])
      setAvgStrokesPerLength(null)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    lengths,
    isLoading,
    error,
    loadWorkoutLengths,
    avgStrokesPerLength,
  }
}
